import type { UIFlagFilter } from '@gonfalon/flag-filters/types';
import { msg } from '@gonfalon/messages/msg';
import { ButtonBadge } from '@gonfalon/ui/Button';
import { Button, DialogTrigger, Popover } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { EventLocation, trackFilterEvent } from '#utils/analyticsUtils';

import { FlagFiltersDialog, type HiddenFlagFilter } from './FlagFiltersDialog';

import styles from './FlagFilters.module.css';

function countActiveHiddenFilters(filters: UIFlagFilter, hiddenFilters: readonly HiddenFlagFilter[]) {
  let count = 0;
  if (hiddenFilters.includes('applicationEvaluated') && filters.applicationEvaluated) {
    count++;
  }
  if (hiddenFilters.includes('archivedState') && filters.state === 'archived') {
    count++;
  }
  return count;
}

export function FlagFilters({
  filters,
  activeFilterCount,
  onClear,
  onChange,
  hiddenFilters = [],
}: {
  activeFilterCount: number;
  filters: UIFlagFilter;
  onClear: () => void;
  onChange: (filter: UIFlagFilter) => void;
  hiddenFilters?: readonly HiddenFlagFilter[];
}) {
  const visibleActiveFilterCount = Math.max(0, activeFilterCount - countActiveHiddenFilters(filters, hiddenFilters));

  const handleFilterButtonClick = () => {
    trackFilterEvent('Filter Dropdown Clicked', { location: EventLocation.FLAG_LIST });
  };

  const handleClear = () => {
    trackFilterEvent('Filter Clear All Button Clicked', { location: EventLocation.FLAG_LIST });
    onClear();
  };

  return (
    <DialogTrigger>
      <Button variant="minimal" onPress={handleFilterButtonClick}>
        <Icon name="filter-list" size="small" slot="start-icon" /> {msg('flags.filters.trigger')}
        <Icon name="chevron-down" size="small" slot="end-icon" />
        {visibleActiveFilterCount > 0 ? (
          <ButtonBadge ariaLabel={msg('flags.filters.activeCountLabel', { count: visibleActiveFilterCount })}>
            {visibleActiveFilterCount}
          </ButtonBadge>
        ) : undefined}
      </Button>
      <Popover placement="bottom end" className={styles.popover}>
        <FlagFiltersDialog
          filters={filters}
          onClear={visibleActiveFilterCount > 0 ? handleClear : undefined}
          onChange={onChange}
          hiddenFilters={hiddenFilters}
        />
      </Popover>
    </DialogTrigger>
  );
}
